import { useCallback, useEffect, useState } from 'react';

import type { UiMilestone } from '@nightshift/ui-types';

import { Empty, Kicker, Spinner } from '../components/primitives.tsx';
import { api, type SessionUser } from '../live/api.ts';
import { useLive, useStore } from '../live/context.tsx';
import { canWriteSettings } from '../lib/privilege.ts';

/** Matches MILESTONE_DESCRIPTION_MAX in core; the dashboard cannot import core. The server enforces it. */
const DESCRIPTION_MAX = 4000;

function describeError(caught: unknown): string {
  return caught instanceof Error ? caught.message : String(caught);
}

interface MilestoneDraft {
  id: number;
  name: string;
  description: string;
  /** The phase this milestone belongs to; empty for none. */
  phase: string;
  /** Milestone ids this one comes after. */
  after: number[];
}

export function MilestonesSettingsPanel({ user }: { user: SessionUser | null }) {
  const store = useStore();
  const state = useLive();
  const canWrite = canWriteSettings(user);
  const project = state.projectSlug;
  const [items, setItems] = useState<UiMilestone[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [busy, setBusy] = useState(false);
  const [editing, setEditing] = useState<MilestoneDraft | null>(null);
  const [editError, setEditError] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      const listed = await api.milestones(project);
      setItems(listed.milestones);
      setError(null);
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : String(caught));
    }
  }, [project]);

  useEffect(() => {
    void load();
  }, [load]);

  if (error && !items) {
    return (
      <Empty
        title="Milestones could not be read."
        body={error}
        action={
          <button type="button" className="btn" onClick={() => void load()}>
            Try again
          </button>
        }
      />
    );
  }

  if (!items) {
    return (
      <div style={{ display: 'grid', placeItems: 'center', padding: 'var(--s8)' }}>
        <Spinner size={20} />
      </div>
    );
  }

  const create = async () => {
    if (!project || name.trim().length === 0) return;
    setBusy(true);
    try {
      await api.createMilestone({ project, name: name.trim(), description });
      store.toast('good', `Milestone ${name.trim()} saved.`);
      setName('');
      setDescription('');
      await load();
    } catch (caught) {
      store.toast('bad', describeError(caught));
    } finally {
      setBusy(false);
    }
  };

  const startEdit = (row: UiMilestone) => {
    setEditing({ id: row.id, name: row.name, description: row.detail ?? '', phase: row.phase ?? '', after: row.after.map((entry) => entry.id) });
    setEditError(null);
  };

  const cancelEdit = () => {
    setEditing(null);
    setEditError(null);
  };

  const saveEdit = async () => {
    if (!editing) return;
    setBusy(true);
    try {
      const saved = await api.updateMilestone(editing.id, {
        name: editing.name,
        description: editing.description,
        phase: editing.phase,
        after: editing.after,
      });
      store.toast('good', `Milestone ${saved.milestone.name} saved.`);
      setEditing(null);
      setEditError(null);
      await load();
    } catch (caught) {
      setEditError(describeError(caught));
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="sheet-panel milestones-settings-panel">
      <header className="panel-head">
        <Kicker count={items.length}>Milestones</Kicker>
      </header>
      <div className="sheet-panel-body">
        <p className="cfg-help" style={{ marginTop: 0 }}>
          {project
            ? 'Named labels for this project. Rename or describe a milestone here; its tickets stay with it. A milestone can come after others. Blocking order holds its tickets until those milestones are finished, so dependencies between them need no ticket links; order only places it after them without holding anything. Draw both kinds in Graph, Milestones. Milestones with no order are parallel tracks. There is no archive column, so milestones cannot be removed. Create Ticket uses the same POST.'
            : 'Showing milestones across every project. Select a project in the header to add a milestone.'}
          {canWrite ? null : ' Only an admin can change them here.'}
        </p>
        {items.length === 0 ? (
          <p className="sheet-note">No milestones yet.</p>
        ) : (
          items.map((row) =>
            editing?.id === row.id ? (
              <div className="cfg-field cfg-field-wide" key={row.id} data-milestone-editor={row.id}>
                <div style={{ display: 'grid', gap: 'var(--s2)', minWidth: 0 }}>
                  <input
                    className="input"
                    aria-label="Milestone name"
                    value={editing.name}
                    disabled={busy}
                    onChange={(e) => setEditing({ ...editing, name: e.target.value })}
                  />
                  <textarea
                    className="input"
                    aria-label="Milestone description"
                    placeholder="Description (optional)"
                    value={editing.description}
                    disabled={busy}
                    maxLength={DESCRIPTION_MAX}
                    rows={4}
                    onChange={(e) => setEditing({ ...editing, description: e.target.value })}
                    style={{ width: '100%', resize: 'vertical' }}
                  />
                  <input
                    className="input"
                    aria-label="Milestone phase"
                    placeholder="Phase (optional): milestones in one phase share a box in Graph, Milestones"
                    value={editing.phase}
                    disabled={busy}
                    maxLength={80}
                    list="milestone-phase-options"
                    onChange={(e) => setEditing({ ...editing, phase: e.target.value })}
                  />
                  <datalist id="milestone-phase-options">
                    {[...new Set(items.filter((other) => other.projectSlug === row.projectSlug && other.phase).map((other) => other.phase!))]
                      .map((phase) => <option key={phase} value={phase} />)}
                  </datalist>
                  <fieldset className="milestone-order-editor" disabled={busy} style={{ border: 0, margin: 0, padding: 0, minWidth: 0 }}>
                    <legend className="cfg-label">Comes after, blocking start</legend>
                    {items.filter((other) => other.id !== editing.id && other.projectSlug === row.projectSlug).length === 0 ? (
                      <p className="cfg-help">No other milestones in this project.</p>
                    ) : (
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 'var(--s1) var(--s3)' }}>
                        {items
                          .filter((other) => other.id !== editing.id && other.projectSlug === row.projectSlug)
                          .map((other) => (
                            <label key={other.id} className="cfg-help" style={{ display: 'inline-flex', gap: 'var(--s1)', alignItems: 'center' }}>
                              <input
                                type="checkbox"
                                checked={editing.after.includes(other.id)}
                                onChange={(event) => setEditing({
                                  ...editing,
                                  after: event.target.checked
                                    ? [...editing.after, other.id]
                                    : editing.after.filter((id) => id !== other.id),
                                })}
                              />
                              {other.name}
                            </label>
                          ))}
                      </div>
                    )}
                  </fieldset>
                  {editError ? (
                    <p className="cfg-alert" role="alert" style={{ margin: 0 }}>
                      Not saved: {editError}
                    </p>
                  ) : null}
                  <div className="cfg-control">
                    <button type="button" className="btn btn-sm" disabled={busy} onClick={cancelEdit}>
                      Cancel
                    </button>
                    <button
                      type="button"
                      className="btn btn-accent btn-sm"
                      disabled={busy}
                      onClick={() => void saveEdit()}
                    >
                      Save
                    </button>
                  </div>
                </div>
              </div>
            ) : (
              <div className="cfg-field" key={row.id} data-milestone-row={row.id}>
                <div style={{ minWidth: 0 }}>
                  <span className="cfg-label">{row.name}</span>
                  {row.detail ? (
                    <p className="cfg-help" style={{ whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }}>
                      {row.detail}
                    </p>
                  ) : null}
                  <p className="cfg-help">
                    {row.ticketCount} ticket{row.ticketCount === 1 ? '' : 's'}
                    {row.openCount > 0 ? ` · ${row.openCount} open` : ''}
                  </p>
                  {row.after.length > 0 ? (
                    <p className="cfg-help" data-milestone-after={row.id}>
                      Comes after {row.after.map((entry) => entry.name).join(', ')}
                    </p>
                  ) : null}
                  {row.phase ? (
                    <p className="cfg-help" data-milestone-phase={row.id}>Phase: {row.phase}</p>
                  ) : null}
                  {row.orderAfter.length > 0 ? (
                    <p className="cfg-help" data-milestone-order-after={row.id}>
                      Planned after {row.orderAfter.map((entry) => entry.name).join(', ')} (order only)
                    </p>
                  ) : null}
                </div>
                {canWrite ? (
                  <div className="cfg-control">
                    <button
                      type="button"
                      className="btn btn-sm"
                      aria-label={`Edit ${row.name}`}
                      disabled={busy || editing !== null}
                      onClick={() => startEdit(row)}
                    >
                      Edit
                    </button>
                  </div>
                ) : null}
              </div>
            ),
          )
        )}
        {project ? (
          <div style={{ display: 'grid', gap: 'var(--s2)', marginTop: 'var(--s3)' }}>
            <input
              className="input"
              value={name}
              disabled={!canWrite || busy}
              onChange={(e) => setName(e.target.value)}
              placeholder="Milestone name"
              onKeyDown={(event) => {
                if (event.key === 'Enter') void create();
              }}
            />
            <textarea
              className="input"
              aria-label="New milestone description"
              placeholder="Description (optional)"
              value={description}
              disabled={!canWrite || busy}
              maxLength={DESCRIPTION_MAX}
              rows={2}
              onChange={(e) => setDescription(e.target.value)}
              style={{ width: '100%', resize: 'vertical' }}
            />
            <div className="cfg-control">
              <button
                type="button"
                className="btn btn-accent btn-sm"
                disabled={!canWrite || busy || name.trim().length === 0}
                onClick={() => void create()}
              >
                Add
              </button>
            </div>
          </div>
        ) : null}
      </div>
    </section>
  );
}
